import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Skeleton,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import { Clear, Camera as CameraIcon, Sort, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { UserMemory } from '../types/Memory';
import { UserProfile } from '../types/UserProfile';
import { getUserMemories } from '../services/api';
import MemoryItem from './memory/MemoryItem';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import AdvertisementResponsive from './advertisement/Ad_GA_res';
import Tips from './dialogs/Tips';

interface MemoriesTabProps {
  username: string;
  isPublic?: boolean;
  userProfile?: UserProfile | null;
}

const MemoriesTab: React.FC<MemoriesTabProps> = ({ username, isPublic = false, userProfile = null }) => {
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));

  // 並び替えオプション
  const sortOptions = [
    { value: 'default', label: '投稿日時 (新しい順)' },
    { value: 'created_at_asc', label: '投稿日時 (古い順)' },
    { value: 'play_date_desc', label: 'プレイ時期 (新しい順)' },
    { value: 'play_date_asc', label: 'プレイ時期 (古い順)' },
    { value: 'updated_at_desc', label: '更新日時 (新しい順)' },
    { value: 'updated_at_asc', label: '更新日時 (古い順)' },
    // { value: 'like_count_desc', label: 'いいね数 (多い順)' },
    // { value: 'title_asc', label: 'タイトル (A-Z)' },
  ];

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value);
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      if (event.target.value !== 'default') {
        params.set('order', event.target.value);
      } else {
        params.delete('order');
      }
      return params;
    });
  };

  const handleSearch = (newQuery: string) => {
    setSearchQuery(newQuery);
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      if (newQuery) {
        params.set('query', newQuery);
      } else {
        params.delete('query');
      }
      return params;
    });
  };

  const handleClearFilter = () => {
    setSearchInput('');
    setSearchQuery('');
    setSortOrder('default');
    setPage(1);
    setSearchParams(params => {
      params.set('page', '1');
      params.delete('query');
      params.delete('order');
      return params;
    });
  };



  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    setSearchParams(params => {
      params.set('page', value.toString());
      return params;
    });
  };

  useEffect(() => {
    // URLパラメータから初期値を設定
    const queryParam = searchParams.get('query') || '';
    const orderParam = searchParams.get('order') || 'default';
    
    setSearchQuery(queryParam);
    setSearchInput(queryParam);
    setSortOrder(orderParam);
  }, []);

  useEffect(() => {
    const fetchMemories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const queryParam = searchParams.get('query') || searchQuery;
        const orderParam = searchParams.get('order') || sortOrder;
        
        const response = await getUserMemories(username, page, queryParam, orderParam);
        setMemories(response.data.results || []);
        
        // ページネーション情報を設定（バックエンドのpage_sizeに合わせる）
        const itemsPerPage = 10;
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        
      } catch (error) {
        console.error('メモリーの取得に失敗しました:', error);
        setError('メモリーの読み込みに失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchMemories();
  }, [username, page, searchParams]);

  const handleMemoryUpdate = () => {
    // メモリーが更新された際のコールバック（いいねなど）
    // 必要に応じて再取得を行う
  };

  const MemoryLoadingPlaceholder = () => {
    return (
      <Box>
        {[...Array(3)].map((_, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Skeleton 
              variant="rectangular" 
              height={140} 
              sx={{ 
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }} 
            />
          </Box>
        ))}
      </Box>
    );
  };

  const MemoryTipsContent = () => (
    <Box sx={{ maxWidth: 400 }}>
      <Typography variant="body2" paragraph>
        メモリーは、ゲームを評価（レビュー）ではなく、思い出ベースで記録できる機能です。
      </Typography>
      <Typography variant="body2" paragraph>
        当時のことを思い返して、そのゲームにまつわる体験や印象を自由に記録してみましょう。
      </Typography>
      <Typography variant="body2" paragraph>
        使い方：
      </Typography>
      <Box component="ol" sx={{ pl: 2, mt: 1 }}>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            各ゲームページのメモリーボタン（<CameraIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />）をクリックしてダイアログを開きます
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            内容を入力して思い出を記録します
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ゲームステータスのプレイ日時が自動で適用されます。予めゲームをライブラリに保存する必要があります。
          </Typography>
        </li>
        <li>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ネタバレを含む部分は必ずスポイラータグ（フォーマット参照）で囲んでください。
          </Typography>
        </li>
      </Box>
      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
        ※メモリーは現在プロフィールページでのみ表示されますが、将来的には新着メモリーや検索など、他のページでも表示される予定です。
      </Typography>
    </Box>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={8.5}>
        {/* ヘッダー */}
        {/* <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          mb: 2,
          p: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: (theme) => `0 2px 8px ${theme.palette.primary.main}40`
          }}>
            <MemoryIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h2" sx={{ 
              fontWeight: 600,
              color: '#fff',
              fontSize: '1.1rem',
              mb: 0.25
            }}>
              思い出
            </Typography>
            {totalCount > 0 && (
              <Chip 
                label={`${totalCount}件`} 
                size="small" 
                sx={{
                  background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}33, ${theme.palette.primary.main}66)`,
                  color: (theme) => theme.palette.primary.light,
                  border: (theme) => `1px solid ${theme.palette.primary.main}50`,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: 20
                }}
              />
            )}
          </Box>
        </Box> */}

        {/* フィルター・検索・並び替えエリア */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              placeholder="Search..."
              variant="standard"
              size="small"
              value={searchInput}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchInput(newValue);
                handleSearch(newValue);
              }}
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-input': {
                  ...theme.typography.body2,
                },
                '& .MuiInputBase-input::placeholder': {
                  opacity: 0.5
                },
                '& .MuiInputBase-root:before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiInputBase-root:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.8)',
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                displayEmpty
                sx={{
                  minWidth: 100,
                  ...theme.typography.body2,
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  '& .MuiSelect-select': {
                    paddingY: 0.5,
                    paddingX: 1,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  }
                }}
                                                 renderValue={(selected) => {
                  const selectedOption = sortOptions.find(option => option.value === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Sort sx={{ 
                        mr: 0.5, 
                        fontSize: '1rem', 
                        display: { xs: 'none', sm: 'block' } 
                      }} />
                      {selectedOption ? selectedOption.label : 'デフォルト'}
                    </Box>
                  );
                }}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} dense>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Tips
                title="TIPS:メモリー"
                content={<MemoryTipsContent />}
                buttonComponent={
                  <IconButton
                    size="small"
                    sx={{
                      padding: '4px',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                }
              />
            </Box>
          </Box>
                     {searchQuery !== '' && (
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                 {totalCount}件のメモリーが見つかりました
               </Typography>
               <Button
                 size="small"
                 onClick={handleClearFilter}
                 startIcon={<Clear fontSize="small" />}
                 sx={{ ...theme.typography.body2, textTransform: 'none', p: '2px 8px' }}
               >
                 Clear filter
               </Button>
             </Box>
           )}
        </Box>

        {/* メモリーリスト */}
        {loading ? (
          <MemoryLoadingPlaceholder />
        ) : error ? (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: '#FFCDD2',
              fontSize: '0.8rem'
            }}
          >
            {error}
          </Alert>
        ) : memories.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}>
              <CameraIcon sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.4)' }} />
            </Box>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 400, fontSize: '0.85rem' }}>
              {searchQuery || sortOrder !== 'default' 
                ? '条件に一致するメモリーが見つかりませんでした' 
                : 'まだメモリーが投稿されていません'
              }
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              {!isPublic && !searchQuery && sortOrder === 'default' && 
                'ゲームの思い出を記録してみましょう！'
              }
            </Typography>
          </Box>
        ) : (
          <Box>
            {memories.map((memory) => (
              <MemoryItem
                key={memory.id}
                memory={memory}
                onUpdate={handleMemoryUpdate}
                showUserInfo={false}
                userProfile={userProfile}
                isPublic={isPublic}
              />
            ))}

            {/* ページネーション */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="medium"
                />
              </Box>
            )}
          </Box>
        )}
      </Grid>

      {/* 右カラムに広告エリア */}
      <Grid item xs={12} md={3.5} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ position: 'sticky', top: 80 }}>
          <AdvertisementResponsive />
        </Box>
      </Grid>
    </Grid>
  );
};

export default MemoriesTab; 